import { useMemo } from "react";
import { ModalProps } from "@chakra-ui/react";
import { AddIcon, CheckCircleIcon } from "@chakra-ui/icons";
import {
  ColumnPinningState,
  createColumnHelper,
  TableMeta,
} from "@tanstack/react-table";
import { compact } from "lodash";
import OrderingPlatformDirectoryModal from "components/Shared/OrderingPlatformDirectory/Modal";
import {
  Datum,
  DEFAULT_COLUMNS,
  DEFAULT_PINNED_COLUMNS,
} from "components/Shared/OrderingPlatformDirectory/Table";
import { createTableMetaHelper } from "components/Shared/ChakraReactTable/utils";
import IsCustomBadge from "components/Shared/OrderingPlatformDirectory/IsCustomBadge";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { gql } from "__generated__/gql";
import AddOrderingPlatformButton, {
  AddOrderingPlatformPlusButton,
} from "./AddToBrandButton";

const query = gql(/* GraphQL */ `
  query AddBrandOrderingPlatformDirectory($brandId: UUID!) {
    brandOrderingPlatformsList(
      condition: { brandId: $brandId }
      orderBy: [CREATED_AT_ASC]
    ) {
      id
      orderingPlatformId
    }
  }
`);

interface Meta extends TableMeta<Datum> {
  addedOrderingPlatformIds: UUID[] | null | undefined;
}

const col = createColumnHelper<Datum>();
const metaFor = createTableMetaHelper<Datum, Meta>();

const columns = [
  ...DEFAULT_COLUMNS,
  col.accessor((r) => !!r.forBrandId, {
    id: "isCustom",
    header: "Custom?",
    cell: (cell) => <IsCustomBadge orderingPlatform={cell.row.original} />,
  }),
  col.display({
    id: "actions",
    cell: (cell) => {
      const { id } = cell.row.original;
      const { addedOrderingPlatformIds } = metaFor.cell(cell) || {};
      if (addedOrderingPlatformIds?.includes(id)) {
        return <CheckCircleIcon boxSize={6} color="green" />;
      }
      return <AddOrderingPlatformPlusButton orderingPlatformId={id} />;
    },
    size: 67,
    meta: { props: { textAlign: "center" } },
  }),
];

const pinnedColumns: ColumnPinningState = {
  ...DEFAULT_PINNED_COLUMNS,
  right: ["actions"],
};

type Props = Pick<ModalProps, "isOpen" | "onClose">;

export default function DirectoryModal({ isOpen, onClose }: Props) {
  const brandId = useCurrentBrandId();

  const { data: brandOrderingPlatformsData } = useBrandQuery(query, {
    skip: !brandId || !isOpen,
    variables: brandId ? { brandId } : undefined,
  });

  const addedOrderingPlatformIds = useMemo(
    () =>
      compact(
        brandOrderingPlatformsData?.brandOrderingPlatformsList?.map(
          (r) => r.orderingPlatformId,
        ),
      ),
    [brandOrderingPlatformsData],
  );

  const meta = useMemo<Meta>(
    () => ({ addedOrderingPlatformIds }),
    [addedOrderingPlatformIds],
  );

  return (
    <OrderingPlatformDirectoryModal
      {...{ isOpen, onClose, columns, meta, pinnedColumns }}
      renderDrawerFooter={({ orderingPlatformId }) => {
        const isAdded = addedOrderingPlatformIds.includes(orderingPlatformId);
        return (
          <AddOrderingPlatformButton
            orderingPlatformId={orderingPlatformId}
            isDisabled={isAdded}
            colorScheme="pinkButton"
            leftIcon={isAdded ? undefined : <AddIcon boxSize=".9em" />}
          >
            {isAdded ? "Customer Added" : "Add Customer"}
          </AddOrderingPlatformButton>
        );
      }}
    />
  );
}
